import React, { startTransition, useEffect, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { getDatabase, ref, get } from "firebase/database";
import { toast } from "react-toastify";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiExternalLink,
  FiPauseCircle,
  FiPlayCircle,
  FiRefreshCw,
  FiSave,
  FiEdit3,
  FiInfo,
  FiPlus,
  FiTrash2,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
  FiLink,
  FiUpload,
  FiLoader,
  FiX,
} from "react-icons/fi";
import {
  fetchFacebookCommentConfigs,
  createFacebookCommentConfig,
  updateFacebookCommentConfig,
  pauseFacebookCommentConfig,
  resumeFacebookCommentConfig,
  fetchFacebookCommentThreads,
  fetchLeadAnalytics,
  retryFacebookCommentThread,
  fetchCommentQuota,
  connectTelegramGroup,
  disconnectTelegramGroup,
  extractKnowledgeFromFile,
  extractKnowledgeFromUrl,
  getErrorMessage,
  testTelegramAlert,
} from "./services/facebookCommentService";
import { FACEBOOK_COMMENT_REQUIRED_SCOPES } from "./utils/facebookCommentPermissions";

const KNOWLEDGE_CONTEXT_CHAR_LIMIT = 5000;
const KNOWLEDGE_FILE_SIZE_LIMIT_BYTES = 10 * 1024 * 1024;
const KNOWLEDGE_SOURCE_SUPPORTED_TYPES = [
  "application/pdf",
  "text/plain",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

function normalizeKnowledgeText(value) {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function trimKnowledgeText(value, maxLength) {
  const normalized = normalizeKnowledgeText(value);
  if (normalized.length <= maxLength) {
    return normalized;
  }

  const sliced = normalized.slice(0, maxLength).trim();
  const lastWhitespaceIndex = sliced.lastIndexOf(" ");

  if (lastWhitespaceIndex > maxLength * 0.7) {
    return `${sliced.slice(0, lastWhitespaceIndex).trim()}...`;
  }

  return `${sliced.replace(/[.,;:!?]?$/, "").trim()}...`;
}

function buildKnowledgeContextValue(businessContext = "", knowledgeSources = []) {
  return [normalizeKnowledgeText(businessContext)]
    .concat(
      (knowledgeSources || []).map((source) => normalizeKnowledgeText(source?.text))
    )
    .filter(Boolean)
    .join("\n\n");
}

function getKnowledgeSourceLabel(source) {
  if (source?.type === "url") {
    return source?.title || source?.url || "Nguồn URL";
  }

  return source?.name || "Tài liệu";
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const [, base64 = ""] = result.split(",");
      resolve(base64);
    };
    reader.onerror = () => {
      reject(new Error("Không thể đọc file đã chọn"));
    };
    reader.readAsDataURL(file);
  });
}

const GUARDRAIL_PRESETS = {
  safe: {
    label: "🐢 An toàn",
    description: "Trả lời chậm, ưu tiên an toàn tài khoản. Phù hợp page mới.",
    maxRepliesPerUserPer24h: 3,
    maxAutoRepliesPerPostPerHour: 6,
    dailyPageReplyCap: 30,
    cooldownPerUserMinutes: 5,
    minDelayBetweenRepliesSeconds: 60,
  },
  balanced: {
    label: "⚡ Cân bằng",
    description: "Tốc độ vừa phải, phù hợp hầu hết fanpage.",
    maxRepliesPerUserPer24h: 5,
    maxAutoRepliesPerPostPerHour: 10,
    dailyPageReplyCap: 50,
    cooldownPerUserMinutes: 3,
    minDelayBetweenRepliesSeconds: 45,
  },
  fast: {
    label: "🚀 Nhanh",
    description: "Trả lời nhanh nhất, phù hợp page lớn hoặc chạy quảng cáo.",
    maxRepliesPerUserPer24h: 8,
    maxAutoRepliesPerPostPerHour: 20,
    dailyPageReplyCap: 100,
    cooldownPerUserMinutes: 1,
    minDelayBetweenRepliesSeconds: 25,
  },
};

function detectPresetFromGuardrails(guardrails) {
  for (const [key, preset] of Object.entries(GUARDRAIL_PRESETS)) {
    if (
      Number(guardrails.cooldownPerUserMinutes) === preset.cooldownPerUserMinutes &&
      Number(guardrails.minDelayBetweenRepliesSeconds) === preset.minDelayBetweenRepliesSeconds &&
      Number(guardrails.dailyPageReplyCap) === preset.dailyPageReplyCap
    ) {
      return key;
    }
  }
  return "custom";
}

function createDraftConfig() {
  return {
    id: null,
    name: "Lead Conversion Engine",
    enabled: false,
    mode: "hybrid",
    goalPriority: "lead_first",
    selectedPages: [],
    businessContext: "",
    knowledgeSources: [],
    brandVoice: "",
    contactInfo: "",
    phone: "",
    address: "",
    ctaTemplatesText:
      "Bạn inbox fanpage để mình tư vấn chi tiết ngay nhé.\nBạn nhắn tin trực tiếp cho fanpage để mình hỗ trợ nhanh nhất.",
    faqRules: [{ question: "", answer: "" }],
    blockedKeywordsText: "",
    autoReplyPolicies: {
      price: true,
      availability: true,
      contact_request: true,
      generic_engagement: false,
    },
    replySpeedPreset: "balanced",
    guardrails: {
      maxRepliesPerUserPer24h: 5,
      maxAutoRepliesPerPostPerHour: 10,
      dailyPageReplyCap: 50,
      cooldownPerUserMinutes: 3,
      minDelayBetweenRepliesSeconds: 45,
      quietHours: {
        enabled: false,
        start: "23:00",
        end: "06:00",
        timezone: "Asia/Ho_Chi_Minh",
      },
      requiresReviewForLeadHot: true,
      requiresReviewForSupportAmbiguous: true,
    },
    ai: {
      provider: "gemini",
      model: "gemini-3.1-flash-lite",
      maxTokens: 300,
      temperature: 0.4,
      billingEnabled: false,
    },
    telegramAlert: {
      enabled: false,
      chatId: null,
      groupName: "",
      connectedAt: null,
      alertOnHotLead: true,
      alertOnWarmLead: false,
      alertWhenManualFollowUp: true,
      notifyAdmin: false,
    },
  };
}

function inflateConfig(config) {
  const draft = createDraftConfig();
  return {
    ...draft,
    ...config,
    businessContext: config.businessContext || "",
    knowledgeSources: Array.isArray(config.knowledgeSources)
      ? config.knowledgeSources
      : [],
    phone: config.phone || config.contactInfo || "",
    address: config.address || "",
    selectedPages: (config.selectedPages || []).map((page) => page.id || page),
    ctaTemplatesText: (config.ctaTemplates || draft.ctaTemplatesText.split("\n")).join("\n"),
    faqRules: Array.isArray(config.faqRules) && config.faqRules.length > 0 ? config.faqRules : [{ question: "", answer: "" }],
    blockedKeywordsText: (config.blockedKeywords || []).join(", "),
    autoReplyPolicies: {
      ...draft.autoReplyPolicies,
      ...(config.autoReplyPolicies || {}),
    },
    replySpeedPreset: config.replySpeedPreset || detectPresetFromGuardrails({
      ...draft.guardrails,
      ...(config.guardrails || {}),
    }),
    guardrails: {
      ...draft.guardrails,
      ...(config.guardrails || {}),
      quietHours: {
        ...draft.guardrails.quietHours,
        ...(config.guardrails?.quietHours || {}),
      },
    },
    ai: {
      ...draft.ai,
      ...(config.ai || {}),
    },
    telegramAlert: {
      ...draft.telegramAlert,
      ...(config.telegramAlert || {}),
    },
  };
}

function serializeConfig(formState, connectedFanpages) {
  const pageNameMap = new Map(
    connectedFanpages.map((page) => [page.id, page.name || ""])
  );

  return {
    name: formState.name,
    enabled: Boolean(formState.enabled),
    mode: "hybrid",
    goalPriority: "lead_first",
    selectedPages: (formState.selectedPages || []).map((pageId) => ({
      id: pageId,
      name: pageNameMap.get(pageId) || "",
    })),
    businessContext: formState.businessContext,
    knowledgeSources: (formState.knowledgeSources || [])
      .map((source) => ({
        id: source.id,
        type: source.type,
        text: normalizeKnowledgeText(source.text),
        charCount: normalizeKnowledgeText(source.text).length,
        name: source.name || "",
        title: source.title || "",
        url: source.url || "",
        mimeType: source.mimeType || "",
        createdAt: source.createdAt || null,
      }))
      .filter((source) => source.text),
    brandVoice: formState.brandVoice,
    contactInfo: formState.phone || formState.contactInfo,
    phone: formState.phone || formState.contactInfo,
    address: formState.address || "",
    ctaTemplates: formState.ctaTemplatesText
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean),
    faqRules: (formState.faqRules || [])
      .map(rule => ({
        question: (rule.question || "").trim(),
        answer: (rule.answer || "").trim(),
      }))
      .filter(rule => rule.question || rule.answer),
    blockedKeywords: formState.blockedKeywordsText
      .split(",")
      .map((keyword) => keyword.trim())
      .filter(Boolean),
    autoReplyPolicies: {
      ...formState.autoReplyPolicies,
    },
    guardrails: {
      ...formState.guardrails,
      maxRepliesPerUserPer24h: Number(
        formState.guardrails.maxRepliesPerUserPer24h
      ),
      maxAutoRepliesPerPostPerHour: Number(
        formState.guardrails.maxAutoRepliesPerPostPerHour
      ),
      dailyPageReplyCap: Number(formState.guardrails.dailyPageReplyCap),
      cooldownPerUserMinutes: Number(
        formState.guardrails.cooldownPerUserMinutes
      ),
      minDelayBetweenRepliesSeconds: Number(
        formState.guardrails.minDelayBetweenRepliesSeconds
      ),
    },
    ai: {
      ...formState.ai,
      provider: "gemini",
      maxTokens: Number(formState.ai.maxTokens),
      temperature: Number(formState.ai.temperature),
      billingEnabled: false,
    },
    telegramAlert: {
      enabled: Boolean(formState.telegramAlert?.enabled),
      chatId: formState.telegramAlert?.chatId || null,
      groupName: formState.telegramAlert?.groupName || "",
      connectedAt: formState.telegramAlert?.connectedAt || null,
      alertOnHotLead: formState.telegramAlert?.alertOnHotLead !== false,
      alertOnWarmLead: Boolean(formState.telegramAlert?.alertOnWarmLead),
      alertWhenManualFollowUp:
        formState.telegramAlert?.alertWhenManualFollowUp !== false,
      notifyAdmin: Boolean(formState.telegramAlert?.notifyAdmin),
    },
  };
}

function buildPageStatus(page) {
  const missingScopes = FACEBOOK_COMMENT_REQUIRED_SCOPES.filter(
    (scope) => !(page.grantedScopes || []).includes(scope)
  );

  return {
    eligible: Boolean(page.commentAutomationEligible),
    missingScopes,
  };
}

function formatRelativeTime(timestamp) {
  if (!timestamp) {
    return "Chưa có";
  }

  const diffMinutes = Math.max(1, Math.floor((Date.now() - timestamp) / 60000));
  if (diffMinutes < 60) {
    return `${diffMinutes} phút trước`;
  }

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours} giờ trước`;
  }

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} ngày trước`;
}

const MONITORING_THREAD_LIMIT = 200;

function buildFacebookThreadLink({ pageId, postId, commentId } = {}) {
  if (pageId && postId) {
    const baseUrl = `https://www.facebook.com/${encodeURIComponent(
      pageId
    )}/posts/${encodeURIComponent(postId)}`;
    return commentId
      ? `${baseUrl}?comment_id=${encodeURIComponent(commentId)}`
      : baseUrl;
  }

  if (commentId) {
    return `https://www.facebook.com/${encodeURIComponent(commentId)}`;
  }

  if (pageId) {
    return `https://www.facebook.com/${encodeURIComponent(pageId)}`;
  }

  return "";
}

const ToggleSwitch = ({ checked, onChange, label }) => (
  <div className="flex items-center gap-3 cursor-pointer select-none" onClick={() => onChange(!checked)}>
    <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${checked ? 'bg-blue-600' : 'bg-gray-200'}`}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </div>
    {label && <span className="text-sm text-gray-700">{label}</span>}
  </div>
);

const TooltipInfo = ({ text }) => (
  <div className="group relative inline-block ml-2 align-middle cursor-help">
    <FiInfo className="text-gray-400 hover:text-blue-500 transition-colors w-4 h-4" />
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-72 bg-slate-800 text-white text-xs rounded-xl py-2.5 px-3 shadow-xl z-50 whitespace-normal text-center leading-relaxed">
      {text}
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-[1px] border-4 border-transparent border-t-slate-800"></div>
    </div>
  </div>
);

const FacebookCommentAutomationScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const urlConfigId = searchParams.get("configId");
  const urlMode = searchParams.get("mode");

  const [userId, setUserId] = useState("");
  const [connectedFanpages, setConnectedFanpages] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(
    urlMode === "new" ? "draft" : urlConfigId || "draft"
  );
  const [formState, setFormState] = useState(createDraftConfig());
  const [knowledgeTab, setKnowledgeTab] = useState("text");
  const [extracting, setExtracting] = useState(false);
  const [knowledgeUrlInput, setKnowledgeUrlInput] = useState("");
  const [statusThreads, setStatusThreads] = useState({
    manual_followup: [],
    sent: [],
    ignored: [],
    failed: [],
  });
  const [leadAnalytics, setLeadAnalytics] = useState(null);
  const [activeMainTab, setActiveMainTab] = useState("settings");
  const [activeTab, setActiveTab] = useState("manual_followup");
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processingAction, setProcessingAction] = useState("");
  const [quotaInfo, setQuotaInfo] = useState(null);
  const lastMonitoringScopeRef = useRef("");
  const knowledgeFileInputRef = useRef(null);

  const selectedConfig = selectedConfigId === "draft"
    ? null
    : configs.find((config) => config.id === selectedConfigId) || null;
  const isCommentFeatureLocked = Boolean(quotaInfo && !quotaInfo.hasAccess);

  const selectedPageIds = formState.selectedPages || [];
  const pageStats = leadAnalytics?.byPage || [];
  const manualFollowUpThreads = statusThreads.manual_followup || [];
  const sentThreads = statusThreads.sent || [];
  const ignoredThreads = statusThreads.ignored || [];
  const failedThreads = statusThreads.failed || [];
  const currentTabThreads =
    activeTab === "sent"
      ? sentThreads
      : activeTab === "ignored"
      ? ignoredThreads
      : activeTab === "failed"
      ? failedThreads
      : manualFollowUpThreads;
  const currentTabCount =
    activeTab === "sent"
      ? leadAnalytics?.overview?.currentStatusCounts?.sent || 0
      : activeTab === "ignored"
      ? leadAnalytics?.overview?.currentStatusCounts?.ignored || 0
      : activeTab === "failed"
      ? leadAnalytics?.overview?.currentStatusCounts?.failed || 0
      : leadAnalytics?.overview?.currentStatusCounts?.manualFollowUp || 0;
  const knowledgeSources = formState.knowledgeSources || [];
  const combinedKnowledgeContext = buildKnowledgeContextValue(
    formState.businessContext,
    knowledgeSources
  );
  const knowledgeContextLength = combinedKnowledgeContext.length;
  const knowledgeContextRemaining =
    KNOWLEDGE_CONTEXT_CHAR_LIMIT - knowledgeContextLength;

  const loadConnectedFanpages = async (currentUserId) => {
    const db = getDatabase();
    const snapshot = await get(
      ref(db, `users/${currentUserId}/connectedFanpages`)
    );
    if (!snapshot.exists()) {
      return [];
    }

    return Object.values(snapshot.val()).sort((left, right) =>
      (left.name || "").localeCompare(right.name || "", "vi")
    );
  };

  const loadOperationalData = async (currentUserId, pageIds = []) => {
    const nextLeadAnalytics = await fetchLeadAnalytics(
      currentUserId,
      30,
      pageIds
    ).catch(() => null);
    setLeadAnalytics(nextLeadAnalytics);
  };

  const loadMonitoringTabThreads = async (
    currentUserId,
    status,
    pageIds = []
  ) => {
    const nextThreads = await fetchFacebookCommentThreads(
      currentUserId,
      null,
      status,
      pageIds,
      {
        limit: MONITORING_THREAD_LIMIT,
        view: "monitoring",
      }
    );

    setStatusThreads((current) => ({
      ...current,
      [status]: nextThreads,
    }));
  };

  const loadData = async ({ preserveSelection = true } = {}) => {
    const currentUserId = localStorage.getItem("userId");
    if (!currentUserId) {
      toast.error("Không tìm thấy userId hiện tại");
      setLoading(false);
      return;
    }

    if (!loading) {
      setRefreshing(true);
    }

    try {
      setUserId(currentUserId);
      const [fanpages, nextConfigs] = await Promise.all([
        loadConnectedFanpages(currentUserId),
        fetchFacebookCommentConfigs(currentUserId),
      ]);

      setConnectedFanpages(fanpages);
      setConfigs(nextConfigs);

      let nextSelectedConfigId;
      if (urlMode === "new") {
        nextSelectedConfigId = "draft";
      } else if (urlConfigId && nextConfigs.some((c) => c.id === urlConfigId)) {
        nextSelectedConfigId = urlConfigId;
      } else if (
        preserveSelection &&
        selectedConfigId !== "draft" &&
        nextConfigs.some((config) => config.id === selectedConfigId)
      ) {
        nextSelectedConfigId = selectedConfigId;
      } else {
        nextSelectedConfigId = nextConfigs[0]?.id || "draft";
      }

      setSelectedConfigId(nextSelectedConfigId);
      setFormState(
        nextSelectedConfigId === "draft"
          ? createDraftConfig()
          : inflateConfig(
              nextConfigs.find((config) => config.id === nextSelectedConfigId)
            )
      );
      setKnowledgeTab("text");
      setKnowledgeUrlInput("");

      // Load quota info (non-blocking)
      // fetchCommentQuota(currentUserId)
      //   .then((data) => setQuotaInfo(data))
      //   .catch(() => {});
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể tải cấu hình Lead Conversion Engine"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData({ preserveSelection: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlConfigId, urlMode]);

  useEffect(() => {
    if (!userId || activeMainTab !== "monitoring") {
      return;
    }

    const monitoringScopeKey = `${userId}:${selectedConfigId}:${selectedPageIds.join(",")}`;
    const scopeChanged = lastMonitoringScopeRef.current !== monitoringScopeKey;
    lastMonitoringScopeRef.current = monitoringScopeKey;

    const loadMonitoringView = async () => {
      if (scopeChanged) {
        await loadOperationalData(userId, selectedPageIds);
      }

      await loadMonitoringTabThreads(userId, activeTab, selectedPageIds);
    };

    loadMonitoringView().catch((error) => {
      toast.error(getErrorMessage(error, "Không thể tải dữ liệu vận hành"));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMainTab, userId, selectedConfigId, selectedPageIds.join(","), activeTab]);

  const updateForm = (patch) => {
    setFormState((current) => ({
      ...current,
      ...patch,
    }));
  };

  const updateGuardrails = (patch) => {
    setFormState((current) => ({
      ...current,
      guardrails: {
        ...current.guardrails,
        ...patch,
      },
    }));
  };

  const updateQuietHours = (patch) => {
    setFormState((current) => ({
      ...current,
      guardrails: {
        ...current.guardrails,
        quietHours: {
          ...current.guardrails.quietHours,
          ...patch,
        },
      },
    }));
  };

  const updatePolicies = (key, value) => {
    setFormState((current) => ({
      ...current,
      autoReplyPolicies: {
        ...current.autoReplyPolicies,
        [key]: value,
      },
    }));
  };

  const updateTelegramAlert = (patch) => {
    setFormState((current) => ({
      ...current,
      telegramAlert: {
        ...current.telegramAlert,
        ...patch,
      },
    }));
  };

  const appendKnowledgeSource = (sourcePayload) => {
    const normalizedText = normalizeKnowledgeText(sourcePayload.text);
    if (!normalizedText) {
      toast.error("Gemini chưa trích xuất được nội dung hữu ích từ nguồn này");
      return;
    }

    const separatorLength = combinedKnowledgeContext ? 2 : 0;
    const remainingChars =
      KNOWLEDGE_CONTEXT_CHAR_LIMIT - knowledgeContextLength - separatorLength;

    if (remainingChars <= 0) {
      toast.error(
        "Knowledge Base đã đạt giới hạn 5.000 ký tự. Hãy rút gọn phần mô tả hiện có trước khi thêm nguồn mới."
      );
      return;
    }

    let nextText = normalizedText;
    let wasTrimmed = false;

    if (normalizedText.length > remainingChars) {
      nextText = trimKnowledgeText(normalizedText, remainingChars);
      wasTrimmed = true;
    }

    const nextSource = {
      id: `knowledge-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      type: sourcePayload.type,
      text: nextText,
      charCount: nextText.length,
      name: sourcePayload.name || "",
      title: sourcePayload.title || "",
      url: sourcePayload.url || "",
      mimeType: sourcePayload.mimeType || "",
      createdAt: Date.now(),
    };

    startTransition(() => {
      setFormState((current) => ({
        ...current,
        knowledgeSources: [...(current.knowledgeSources || []), nextSource],
      }));
      setKnowledgeTab("text");
    });

    if (wasTrimmed) {
      toast.info("Nguồn mới đã được thêm nhưng bị rút gọn để giữ tổng Knowledge Base trong 5.000 ký tự.");
      return;
    }

    toast.success("Đã thêm nguồn vào Knowledge Base");
  };

  const handleRemoveKnowledgeSource = (sourceId) => {
    startTransition(() => {
      setFormState((current) => ({
        ...current,
        knowledgeSources: (current.knowledgeSources || []).filter(
          (source) => source.id !== sourceId
        ),
      }));
    });
  };

  const handleKnowledgeFileSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) {
      return;
    }

    if (!KNOWLEDGE_SOURCE_SUPPORTED_TYPES.includes(file.type)) {
      toast.error("Chỉ hỗ trợ file PDF, TXT hoặc DOCX.");
      return;
    }

    if (file.size > KNOWLEDGE_FILE_SIZE_LIMIT_BYTES) {
      toast.error("File vượt quá 10MB. Hãy chọn tài liệu nhỏ hơn.");
      return;
    }

    setExtracting(true);
    try {
      const base64Data = await readFileAsBase64(file);
      const result = await extractKnowledgeFromFile({
        base64Data,
        mimeType: file.type,
        fileName: file.name,
      });

      appendKnowledgeSource({
        type: "file",
        text: result?.text || "",
        name: result?.source?.name || file.name,
        mimeType: result?.source?.mimeType || file.type,
      });
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể trích xuất nội dung từ tài liệu"));
    } finally {
      setExtracting(false);
    }
  };

  const handleExtractKnowledgeFromUrl = async () => {
    const normalizedUrl = knowledgeUrlInput.trim();
    if (!normalizedUrl) {
      toast.error("Hãy nhập URL https:// để trích xuất Knowledge Base.");
      return;
    }

    setExtracting(true);
    try {
      const result = await extractKnowledgeFromUrl(normalizedUrl);

      appendKnowledgeSource({
        type: "url",
        text: result?.text || "",
        url: result?.source?.url || normalizedUrl,
        title: result?.source?.title || "",
      });
      setKnowledgeUrlInput("");
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể trích xuất nội dung từ URL"));
    } finally {
      setExtracting(false);
    }
  };

  const handleConfigChange = async (nextConfigId) => {
    if (nextConfigId === "draft") {
      navigate("/facebook-comment-automation?mode=new");
      return;
    }
    navigate(`/facebook-comment-automation?configId=${nextConfigId}`);
  };

  const handleTogglePage = (pageId) => {
    setFormState((current) => {
      const pageSet = new Set(current.selectedPages || []);
      if (pageSet.has(pageId)) {
        pageSet.delete(pageId);
      } else {
        pageSet.add(pageId);
      }
      return {
        ...current,
        selectedPages: Array.from(pageSet),
      };
    });
  };

  const handleSaveConfig = async () => {
    if (isCommentFeatureLocked) {
      toast.error("Gói hiện tại chưa được sử dụng Lead Conversion Engine. Vui lòng nâng cấp lên gói Chuyên nghiệp trở lên.");
      return;
    }

    if (knowledgeContextLength > KNOWLEDGE_CONTEXT_CHAR_LIMIT) {
      toast.error(
        "Knowledge Base đang vượt giới hạn 5.000 ký tự. Hãy rút gọn trước khi lưu."
      );
      return;
    }

    setSaving(true);
    try {
      const payload = serializeConfig(formState, connectedFanpages);
      const savedConfig =
        selectedConfigId === "draft"
          ? await createFacebookCommentConfig(userId, payload)
          : await updateFacebookCommentConfig(userId, selectedConfigId, payload);

      toast.success("Đã lưu cấu hình Lead Conversion Engine");
      navigate(`/facebook-comment-automation?configId=${savedConfig.id}`);
      setFormState(inflateConfig(savedConfig));
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể lưu cấu hình"));
    } finally {
      setSaving(false);
    }
  };

  const handlePauseResume = async () => {
    if (!selectedConfig) {
      toast.info("Hãy lưu cấu hình trước khi pause hoặc resume");
      return;
    }

    if (isCommentFeatureLocked && !selectedConfig.enabled) {
      toast.error("Gói hiện tại chưa được sử dụng Lead Conversion Engine. Vui lòng nâng cấp lên gói Chuyên nghiệp trở lên.");
      return;
    }

    setProcessingAction(selectedConfig.enabled ? "pause" : "resume");
    try {
      const updatedConfig = selectedConfig.enabled
        ? await pauseFacebookCommentConfig(userId, selectedConfig.id)
        : await resumeFacebookCommentConfig(userId, selectedConfig.id);

        toast.success(
          updatedConfig.enabled
          ? "Đã bật Lead Conversion Engine"
          : "Đã tạm dừng Lead Conversion Engine"
      );
      await loadData({ preserveSelection: false });
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể cập nhật trạng thái cấu hình"));
    } finally {
      setProcessingAction("");
    }
  };

  const handleRetryThread = async (thread) => {
    if (isCommentFeatureLocked) {
      toast.error("Gói hiện tại chưa được sử dụng Lead Conversion Engine. Vui lòng nâng cấp lên gói Chuyên nghiệp trở lên.");
      return;
    }

    setProcessingAction(`retry-${thread.threadKey}`);
    try {
      await retryFacebookCommentThread(userId, thread.pageId, thread.threadKey);
      toast.success("Đã xếp lại thread vào hàng đợi xử lý");
      await Promise.all([
        loadOperationalData(userId, selectedPageIds),
        loadMonitoringTabThreads(userId, activeTab, selectedPageIds),
      ]);
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể retry thread"));
    } finally {
      setProcessingAction("");
    }
  };

  const handleConnectTelegram = async () => {
    if (!selectedConfig?.id) {
      toast.info("Hãy lưu cấu hình trước khi kết nối Telegram group");
      return;
    }

    const chatId = String(formState.telegramAlert?.chatId || "").trim();
    if (!chatId) {
      toast.error("Hãy nhập Chat ID Telegram trước khi kết nối");
      return;
    }

    setProcessingAction("telegram-connect");
    try {
      const result = await connectTelegramGroup(userId, selectedConfig.id, chatId);
      updateTelegramAlert({
        enabled: true,
        chatId: result.chatId,
        groupName: result.groupName,
        connectedAt: result.connectedAt,
      });
      toast.success(`Đã kết nối Telegram group "${result.groupName}"`);
      await loadData({ preserveSelection: true });
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể kết nối Telegram group"));
    } finally {
      setProcessingAction("");
    }
  };

  const handleDisconnectTelegram = async () => {
    if (!selectedConfig?.id) {
      toast.info("Cấu hình chưa được lưu");
      return;
    }

    setProcessingAction("telegram-disconnect");
    try {
      await disconnectTelegramGroup(userId, selectedConfig.id);
      updateTelegramAlert({
        enabled: false,
        chatId: null,
        groupName: "",
        connectedAt: null,
      });
      toast.success("Đã ngắt kết nối Telegram group");
      await loadData({ preserveSelection: true });
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể ngắt kết nối Telegram"));
    } finally {
      setProcessingAction("");
    }
  };

  const handleTestTelegram = async () => {
    if (!selectedConfig?.id) {
      toast.info("Hãy lưu cấu hình trước khi test Telegram");
      return;
    }

    setProcessingAction("telegram-test");
    try {
      await testTelegramAlert(userId, selectedConfig.id);
      toast.success("Đã gửi test alert vào Telegram group");
    } catch (error) {
      toast.error(getErrorMessage(error, "Không thể gửi test alert"));
    } finally {
      setProcessingAction("");
    }
  };

  const handleRefresh = async () => {
    await loadData({ preserveSelection: true });

    if (activeMainTab === "monitoring" && userId) {
      await Promise.all([
        loadOperationalData(userId, selectedPageIds),
        loadMonitoringTabThreads(userId, activeTab, selectedPageIds),
      ]);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center text-gray-600">
          Đang tải cấu hình...
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium mb-4">
              <FiClock />
              AI Comment -> Lead Conversion Engine
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              Tự động ra đơn từ comment
            </h1>
            <p className="text-gray-600 mt-2 max-w-3xl">
              AI tự động nhận diện, ưu tiên và kích hoạt sale action từ comment
              Facebook. Auto reply chỉ là một lớp vận hành, còn giá trị chính là
              giúp bạn nhìn ra lead và chạm đúng lúc.
            </p>
            <a
              href="https://youtu.be/63-P-9G-0d4"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 border border-red-100 text-sm font-medium transition-colors duration-200"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              📺 Xem video hướng dẫn thiết lập
              <FiExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              <FiRefreshCw className={refreshing ? "animate-spin" : ""} />
              Làm mới
            </button>
            <button
              onClick={handlePauseResume}
              disabled={
                !selectedConfig ||
                !!processingAction ||
                (isCommentFeatureLocked && !selectedConfig?.enabled)
              }
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white disabled:opacity-60 ${
                selectedConfig?.enabled
                  ? "bg-amber-500 hover:bg-amber-600"
                  : "bg-emerald-600 hover:bg-emerald-700"
              }`}
            >
              {selectedConfig?.enabled ? <FiPauseCircle /> : <FiPlayCircle />}
              {selectedConfig?.enabled ? "Tạm dừng" : "Kích hoạt"}
            </button>
            <button
              onClick={handleSaveConfig}
              disabled={saving || isCommentFeatureLocked}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              <FiSave />
              Lưu cấu hình
            </button>
          </div>
        </div>
      </div>

      {/* ── Comment Quota Bar ── */}
      {quotaInfo && quotaInfo.hasAccess && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">🤖</span>
              <div>
                <h3 className="text-sm font-semibold text-gray-900">
                  Công suất Lead Engine — Tháng {quotaInfo.currentMonth}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {quotaInfo.unlimited
                    ? "Không giới hạn (VIP)"
                    : `${Number(quotaInfo.used).toLocaleString("vi-VN")} / ${Number(quotaInfo.totalQuota || quotaInfo.quota).toLocaleString("vi-VN")} lượt xử lý đã dùng`}
                  {!quotaInfo.unlimited && quotaInfo.purchasedQuota > 0 && (
                    <span className="ml-1 text-blue-600">(+{Number(quotaInfo.purchasedQuota).toLocaleString("vi-VN")} mua thêm)</span>
                  )}
                </p>
              </div>
            </div>
            {!quotaInfo.unlimited && (
              <div className="flex items-center gap-3 min-w-[200px]">
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      quotaInfo.used / (quotaInfo.totalQuota || quotaInfo.quota) > 0.9
                        ? "bg-red-500"
                        : quotaInfo.used / (quotaInfo.totalQuota || quotaInfo.quota) > 0.7
                        ? "bg-amber-500"
                        : "bg-emerald-500"
                    }`}
                    style={{
                      width: `${Math.min(100, (quotaInfo.used / (quotaInfo.totalQuota || quotaInfo.quota)) * 100)}%`,
                    }}
                  />
                </div>
                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
                  Còn {Number(quotaInfo.remaining).toLocaleString("vi-VN")} lượt xử lý
                </span>
              </div>
            )}
          </div>
          {!quotaInfo.unlimited && quotaInfo.remaining <= 0 && (
            <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-xl flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <p className="text-sm text-red-700">
                ⚠️ Đã hết quota tháng này. Bình luận mới sẽ không được xử lý tự động.
              </p>
              <Link to="/pricing#comment-packages" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 whitespace-nowrap">
                🤖 Mua thêm lượt
              </Link>
            </div>
          )}
          {!quotaInfo.unlimited && quotaInfo.remaining > 0 && quotaInfo.remaining / (quotaInfo.totalQuota || quotaInfo.quota) <= 0.2 && (
            <div className="mt-3 flex items-center justify-between p-3 bg-amber-50 border border-amber-100 rounded-xl">
              <p className="text-sm text-amber-700">
                ⚡ Sắp hết quota ({Number(quotaInfo.remaining).toLocaleString("vi-VN")} lượt còn lại)
              </p>
              <Link to="/pricing#comment-packages" className="text-sm font-semibold text-blue-600 hover:text-blue-800 underline whitespace-nowrap">
                Mua thêm →
              </Link>
            </div>
          )}
        </div>
      )}
      {quotaInfo && !quotaInfo.hasAccess && (
        <div className="bg-amber-50 rounded-2xl border border-amber-200 p-4">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔒</span>
            <div>
              <h3 className="text-sm font-semibold text-amber-900">
                Lead Conversion Engine chưa được kích hoạt
              </h3>
              <p className="text-xs text-amber-700 mt-0.5">
                Nâng cấp lên gói Chuyên nghiệp trở lên để bật engine xử lý lead từ comment.{" "}
                <Link to="/pricing" className="font-semibold underline">
                  Xem bảng giá →
                </Link>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto w-full">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 space-y-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {selectedConfigId === "draft"
                  ? "Tạo cấu hình mới"
                  : `Cấu hình: ${selectedConfig?.name || selectedConfigId}`}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Quản lý rule, hướng dẫn AI, cảnh báo lead và giới hạn an toàn.
              </p>
            </div>
            <div className="flex gap-3">
              <Link
                to="/facebook-comment-automation/configs"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium transition"
              >
                ← Trở về Danh sách
              </Link>
            </div>
          </div>

          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {["settings", "monitoring"].map((mainTab) => (
                <button
                  key={mainTab}
                  onClick={() => setActiveMainTab(mainTab)}
                  className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeMainTab === mainTab
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {mainTab === "settings" && "Cài đặt engine"}
                  {mainTab === "monitoring" && "Lead & Hiệu quả"}
                </button>
              ))}
            </nav>
          </div>

          {activeMainTab === "settings" && (
            <div className="space-y-8 pt-4">
              {/* Section 1: Basic Info */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">1. Thông tin cấu hình</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Tên cấu hình</span>
                    <input
                      value={formState.name}
                      onChange={(event) => updateForm({ name: event.target.value })}
                      className="mt-1 w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                      placeholder="Ví dụ: Engine chốt đơn mỹ phẩm tháng 4"
                    />
                  </label>
                  {selectedConfig && (
                    <div className="flex items-center gap-3 self-end pb-1">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                        selectedConfig.enabled
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-gray-100 text-gray-500"
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${selectedConfig.enabled ? "bg-emerald-500" : "bg-gray-400"}`} />
                        {selectedConfig.enabled ? "Engine đang hoạt động" : "Engine đã tạm dừng"}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Section 2: Page Selection */}
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2 flex-1">2. Fanpage áp dụng</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {connectedFanpages.map((page) => {
                    const pageStatus = buildPageStatus(page);
                    return (
                      <label
                        key={page.id}
                        className={`rounded-2xl border p-4 cursor-pointer transition-colors ${
                          formState.selectedPages.includes(page.id)
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={formState.selectedPages.includes(page.id)}
                            onChange={() => handleTogglePage(page.id)}
                            className="mt-1 w-4 h-4 text-blue-600 rounded"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-gray-900 truncate">
                                {page.name}
                              </p>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                  pageStatus.eligible
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-amber-100 text-amber-700"
                                }`}
                              >
                                {pageStatus.eligible ? <FiCheckCircle /> : <FiAlertCircle />}
                                {pageStatus.eligible ? "Đủ quyền" : "Thiếu quyền"}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-1">{page.id}</p>
                            {!pageStatus.eligible && pageStatus.missingScopes.length > 0 && (
                              <p className="text-xs text-amber-700 mt-2 bg-amber-50 rounded p-2 border border-amber-100">
                                Thiếu quyền: {pageStatus.missingScopes.join(", ")}
                              </p>
                            )}
                            {page.webhookSubscribed === false &&
                              formState.selectedPages.includes(page.id) && (
                                <p className="text-xs text-blue-600 mt-2">
                                  Hệ thống sẽ kết nối lại sau khi bạn lưu cấu hình.
                                </p>
                              )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Section 3: AI Training */}
              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">3. Huấn luyện AI & Knowledge Base</h3>
                <div className="grid grid-cols-1 gap-5">
                  <div className="rounded-2xl border border-gray-200 bg-slate-50/50 p-4 md:p-5 space-y-4">
                    <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-gray-900">
                            Knowledge Base cho AI
                          </span>
                          <TooltipInfo text="Bạn có thể nhập mô tả thủ công hoặc đưa tài liệu/link để Gemini tóm tắt thành nguồn tri thức cho AI comment." />
                          {!combinedKnowledgeContext && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                              ⚠️ Nên bổ sung để AI trả lời sát thực tế hơn
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Tổng dữ liệu dùng cho AI:{" "}
                          <span
                            className={`font-semibold ${
                              knowledgeContextRemaining < 0
                                ? "text-red-600"
                                : knowledgeContextRemaining < 500
                                ? "text-amber-600"
                                : "text-emerald-600"
                            }`}
                          >
                            {knowledgeContextLength.toLocaleString("vi-VN")} / {KNOWLEDGE_CONTEXT_CHAR_LIMIT.toLocaleString("vi-VN")} ký tự
                          </span>
                        </p>
                      </div>

                      <div className="inline-flex flex-wrap gap-2 rounded-2xl bg-white border border-gray-200 p-1">
                        {[
                          { key: "text", icon: <FiEdit3 />, label: "Nhập văn bản" },
                          { key: "file", icon: <FiFileText />, label: "Tải tài liệu" },
                          { key: "url", icon: <FiLink />, label: "Nhập link" },
                        ].map((tab) => (
                          <button
                            key={tab.key}
                            type="button"
                            onClick={() => setKnowledgeTab(tab.key)}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                              knowledgeTab === tab.key
                                ? "bg-blue-600 text-white shadow-sm"
                                : "text-gray-600 hover:bg-gray-100"
                            }`}
                          >
                            {tab.icon}
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {knowledgeTab === "text" && (
                      <label className="block">
                        <span className="text-sm font-medium text-gray-900">
                          Mô tả thủ công về doanh nghiệp / sản phẩm
                        </span>
                        <textarea
                          value={formState.businessContext}
                          onChange={(event) =>
                            updateForm({ businessContext: event.target.value })
                          }
                          maxLength={KNOWLEDGE_CONTEXT_CHAR_LIMIT}
                          rows={4}
                          className={`mt-1.5 w-full px-4 py-3 rounded-xl border transition-all outline-none ${
                            !formState.businessContext?.trim()
                              ? "border-amber-300 bg-amber-50/30 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                              : "border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                          }`}
                          placeholder="Ví dụ: Cửa hàng mỹ phẩm chính hãng, tập trung vào chăm sóc da mụn..."
                        />
                      </label>
                    )}

                    {knowledgeTab === "file" && (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-5">
                        <input
                          ref={knowledgeFileInputRef}
                          type="file"
                          accept=".pdf,.txt,.docx,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                          className="hidden"
                          onChange={handleKnowledgeFileSelected}
                        />
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              Tải tài liệu để Gemini tự tóm tắt
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Hỗ trợ PDF, TXT, DOCX. Giới hạn 10MB mỗi file.
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => knowledgeFileInputRef.current?.click()}
                            disabled={extracting}
                            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                          >
                            {extracting ? <FiLoader className="animate-spin" /> : <FiUpload />}
                            {extracting ? "Đang trích xuất..." : "Chọn tài liệu"}
                          </button>
                        </div>
                      </div>
                    )}

                    {knowledgeTab === "url" && (
                      <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Dán link website / landing page / bài viết sản phẩm
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Chỉ nhận URL bắt đầu bằng <code>https://</code>.
                          </p>
                        </div>
                        <div className="flex flex-col gap-3 md:flex-row">
                          <input
                            type="url"
                            value={knowledgeUrlInput}
                            onChange={(event) =>
                              setKnowledgeUrlInput(event.target.value)
                            }
                            placeholder="https://example.com/san-pham"
                            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                          />
                          <button
                            type="button"
                            onClick={handleExtractKnowledgeFromUrl}
                            disabled={extracting}
                            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                          >
                            {extracting ? <FiLoader className="animate-spin" /> : <FiLink />}
                            {extracting ? "Đang phân tích..." : "Phân tích link"}
                          </button>
                        </div>
                      </div>
                    )}

                    {knowledgeSources.length > 0 && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-sm font-medium text-gray-900">
                            Nguồn Knowledge Base đã thêm
                          </p>
                          <span className="text-xs text-gray-500">
                            {knowledgeSources.length} nguồn
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {knowledgeSources.map((source) => (
                            <div
                              key={source.id}
                              className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800"
                            >
                              {source.type === "url" ? <FiLink className="shrink-0" /> : <FiFileText className="shrink-0" />}
                              <span className="max-w-[240px] truncate font-medium">
                                {getKnowledgeSourceLabel(source)}
                              </span>
                              <span className="text-xs text-blue-600">
                                {Number(source.charCount || source.text?.length || 0).toLocaleString("vi-VN")} ký tự
                              </span>
                              <button
                                type="button"
                                onClick={() => handleRemoveKnowledgeSource(source.id)}
                                className="rounded-full p-1 hover:bg-blue-100"
                                title="Xóa nguồn"
                              >
                                <FiX />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="rounded-2xl border border-gray-200 bg-white p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-sm font-medium text-gray-900">
                          Preview Knowledge Base tổng hợp
                        </p>
                        <span className="text-xs text-gray-500">
                          Cả phần này sẽ được đưa vào prompt AI
                        </span>
                      </div>
                      <textarea
                        value={combinedKnowledgeContext}
                        readOnly
                        rows={6}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-slate-50 text-sm text-gray-700 outline-none resize-y"
                        placeholder="Nội dung sau khi bạn nhập tay hoặc Gemini trích xuất sẽ hiển thị ở đây."
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-900">
                        Phong cách & Giọng điệu
                        <TooltipInfo text="Hướng dẫn AI cách xưng hô (VD: Gọi khách bằng Bạn, xưng Mình) và thái độ khi chat (nhẹ nhàng, chuyên nghiệp, hoặc năng động)." />
                      </span>
                      <textarea
                        value={formState.brandVoice}
                        onChange={(event) => updateForm({ brandVoice: event.target.value })}
                        rows={2}
                        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                        placeholder="Ví dụ: Thân thiện, ngắn gọn, chốt sale nhanh gọn"
                      />
                    </label>
                    <label className="block">
                      <span className="text-sm font-medium text-gray-900">
                        SĐT / Zalo liên hệ
                        <TooltipInfo text="Số điện thoại hoặc Zalo để AI cung cấp khi khách hỏi liên hệ." />
                      </span>
                      <input
                        type="text"
                        value={formState.phone}
                        onChange={(event) => updateForm({ phone: event.target.value })}
                        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                        placeholder="VD: 0394932696"
                      />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-sm font-medium text-gray-900">
                      Địa chỉ cửa hàng <span className="text-gray-400 font-normal">(tuỳ chọn)</span>
                      <TooltipInfo text="Khi khách hỏi địa chỉ, AI sẽ trả lời chính xác thay vì hướng dẫn inbox." />
                    </span>
                    <input
                      type="text"
                      value={formState.address}
                      onChange={(event) => updateForm({ address: event.target.value })}
                      className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                      placeholder="VD: 123 Nguyễn Huệ, Q.1, TP.HCM"
                    />
                  </label>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-900">
                        Mẫu điều hướng khách hàng (CTA)
                        <TooltipInfo text="Các câu AI sẽ mồi để khách hàng inbox hoặc để lại số điện thoại (Mỗi dòng 1 mẫu)." />
                      </span>
                      <textarea
                        value={formState.ctaTemplatesText}
                        onChange={(event) => updateForm({ ctaTemplatesText: event.target.value })}
                        rows={5}
                        className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none text-sm leading-relaxed"
                        placeholder="Bạn inbox page để được tư vấn nhé."
                      />
                    </label>

                    <div className="flex flex-col h-full bg-slate-50/50 rounded-2xl border border-gray-200 p-4">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-semibold text-gray-900">
                          Quy tắc hỏi đáp (FAQ)
                          <TooltipInfo text="Các câu hỏi phổ biến và câu trả lời cố định giúp AI không bị nhầm lẫn (Ví dụ: Giá ship bao nhiêu? -> Trả lời: Freeship toàn quốc)." />
                        </span>
                        <button
                          onClick={() => updateForm({ faqRules: [...formState.faqRules, { question: "", answer: "" }] })}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-lg hover:bg-blue-200 transition-colors"
                        >
                          <FiPlus /> Thêm Q&A
                        </button>
                      </div>
                      <div className="space-y-3 flex-1 overflow-y-auto pr-1" style={{ maxHeight: '200px' }}>
                        {formState.faqRules.map((rule, idx) => (
                          <div key={idx} className="flex gap-2 items-start bg-white p-2.5 rounded-xl border border-gray-200 shadow-sm">
                            <div className="flex-1 space-y-2">
                              <input
                                value={rule.question}
                                onChange={(e) => {
                                  const newRules = [...formState.faqRules];
                                  newRules[idx].question = e.target.value;
                                  updateForm({ faqRules: newRules });
                                }}
                                className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 bg-gray-50 focus:bg-white outline-none"
                                placeholder="Câu hỏi (VD: Shop ở đâu?)"
                              />
                              <input
                                value={rule.answer}
                                onChange={(e) => {
                                  const newRules = [...formState.faqRules];
                                  newRules[idx].answer = e.target.value;
                                  updateForm({ faqRules: newRules });
                                }}
                                className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 outline-none"
                                placeholder="Câu trả lời (VD: Ở Hà Nội ạ)"
                              />
                            </div>
                            <button
                              onClick={() => {
                                const newRules = formState.faqRules.filter((_, i) => i !== idx);
                                updateForm({ faqRules: newRules.length ? newRules : [{ question: "", answer: "" }] });
                              }}
                              className="p-2 text-rose-500 hover:bg-rose-50 rounded-lg transition-colors mt-1"
                              title="Xóa Q&A"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">
                  4. Nhận lead nóng qua Telegram
                </h3>

                <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
                  <p className="text-sm font-medium text-slate-900">
                    Kết nối group Telegram để team sale nhận ngay cơ hội chốt đơn
                    từ comment.
                  </p>
                  <p className="mt-1 text-sm text-slate-600">
                    Phase 1 dùng mô hình đơn giản: bạn nhập Chat ID nếu đã có. Hệ
                    thống sẽ gửi test message để xác nhận bot có thể đẩy alert vào
                    group.
                  </p>
                </div>

                <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
                  <div className="rounded-2xl border border-gray-200 bg-white p-5">
                    <label className="block">
                      <span className="text-sm font-medium text-gray-900">
                        Chat ID Telegram group
                      </span>
                      <input
                        type="text"
                        value={formState.telegramAlert?.chatId || ""}
                        onChange={(event) =>
                          updateTelegramAlert({ chatId: event.target.value })
                        }
                        className="mt-1.5 w-full rounded-xl border border-gray-200 px-4 py-3 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                        placeholder="-100xxxxxxxxxx"
                      />
                    </label>

                    <a
                      href="https://youtube.com/shorts/UrKk7cjIKhI"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 w-full flex items-center gap-2.5 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800 hover:bg-red-100 hover:border-red-300 transition-all cursor-pointer text-left group"
                    >
                      <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg bg-red-200/70 text-red-700 group-hover:bg-red-300/70 transition-colors">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      </span>
                      <div className="flex-1">
                        <p className="font-semibold text-red-900">📹 Xem video hướng dẫn lấy Chat ID</p>
                        <p className="mt-0.5 text-xs text-red-600">Hướng dẫn từng bước trên YouTube — dễ hiểu, nhanh gọn →</p>
                      </div>
                      <FiExternalLink className="w-4 h-4 text-red-400 group-hover:text-red-600 transition-colors" />
                    </a>

                    <div className="mt-4 flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleConnectTelegram}
                        disabled={
                          processingAction === "telegram-connect" ||
                          isCommentFeatureLocked
                        }
                        className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-60"
                      >
                        {processingAction === "telegram-connect" ? (
                          <FiRefreshCw className="animate-spin" />
                        ) : (
                          <FiCheckCircle />
                        )}
                        Kết nối group
                      </button>

                      <button
                        type="button"
                        onClick={handleTestTelegram}
                        disabled={
                          !formState.telegramAlert?.chatId ||
                          processingAction === "telegram-test" ||
                          isCommentFeatureLocked
                        }
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-60"
                      >
                        {processingAction === "telegram-test" ? (
                          <FiRefreshCw className="animate-spin" />
                        ) : (
                          <FiExternalLink />
                        )}
                        Gửi test alert
                      </button>

                      <button
                        type="button"
                        onClick={handleDisconnectTelegram}
                        disabled={
                          !formState.telegramAlert?.connectedAt ||
                          processingAction === "telegram-disconnect"
                        }
                        className="inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2.5 text-sm font-medium text-rose-700 transition hover:bg-rose-50 disabled:opacity-60"
                      >
                        {processingAction === "telegram-disconnect" ? (
                          <FiRefreshCw className="animate-spin" />
                        ) : (
                          <FiAlertCircle />
                        )}
                        Ngắt kết nối
                      </button>
                    </div>

                    {!selectedConfig?.id && (
                      <p className="mt-3 text-xs text-slate-500">
                        Hãy lưu cấu hình trước khi connect Telegram để hệ thống
                        có nơi lưu metadata của group.
                      </p>
                    )}
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-white p-5">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Trạng thái kết nối
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          Group này sẽ nhận lead nóng và case cần sale xử lý.
                        </p>
                      </div>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          formState.telegramAlert?.connectedAt
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {formState.telegramAlert?.connectedAt
                          ? "Đã kết nối"
                          : "Chưa kết nối"}
                      </span>
                    </div>

                    <div className="mt-4 space-y-3 text-sm">
                      <div className="rounded-xl bg-slate-50 px-4 py-3">
                        <p className="text-slate-500">Group</p>
                        <p className="mt-1 font-medium text-slate-900">
                          {formState.telegramAlert?.groupName || "Chưa có"}
                        </p>
                      </div>
                      <div className="rounded-xl bg-slate-50 px-4 py-3">
                        <p className="text-slate-500">Kết nối lúc</p>
                        <p className="mt-1 font-medium text-slate-900">
                          {formState.telegramAlert?.connectedAt
                            ? new Date(
                                formState.telegramAlert.connectedAt
                              ).toLocaleString("vi-VN")
                            : "Chưa kết nối"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 space-y-3 border-t border-gray-100 pt-4">
                      <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Nhận lead nóng
                          </p>
                          <p className="text-xs text-gray-500">
                            Báo ngay khi có tín hiệu mua mạnh.
                          </p>
                        </div>
                        <ToggleSwitch
                          checked={
                            formState.telegramAlert?.alertOnHotLead !== false
                          }
                          onChange={(checked) =>
                            updateTelegramAlert({ alertOnHotLead: checked })
                          }
                        />
                      </label>

                      <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Nhận lead ấm
                          </p>
                          <p className="text-xs text-gray-500">
                            Dùng khi đội sale muốn thấy cả các lead hỏi giá / hỏi
                            mua sớm.
                          </p>
                        </div>
                        <ToggleSwitch
                          checked={Boolean(
                            formState.telegramAlert?.alertOnWarmLead
                          )}
                          onChange={(checked) =>
                            updateTelegramAlert({ alertOnWarmLead: checked })
                          }
                        />
                      </label>

                      <label className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 px-4 py-3">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            Cảnh báo khi cần sale xử lý
                          </p>
                          <p className="text-xs text-gray-500">
                            Dùng cho các case engine không auto reply mà đẩy thẳng
                            cho sale follow-up.
                          </p>
                        </div>
                        <ToggleSwitch
                          checked={
                            formState.telegramAlert?.alertWhenManualFollowUp !==
                            false
                          }
                          onChange={(checked) =>
                            updateTelegramAlert({
                              alertWhenManualFollowUp: checked,
                            })
                          }
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 5: Advanced (Auto Reply Policies & Guardrails) */}
              <div className="mt-8 border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm">
                <button
                  onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                  className="w-full flex items-center justify-between p-5 bg-gray-50/80 hover:bg-gray-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gray-200 text-gray-500">
                      <FiEdit3 className="w-4 h-4" />
                    </span>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900">5. Cài đặt nâng cao & Giới hạn an toàn</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Tùy chỉnh từ khóa cấm, trường hợp kích hoạt bot và giới hạn spam.</p>
                    </div>
                  </div>
                  <div className={`p-2 rounded-full border border-gray-200 bg-white transition-transform duration-300 ${isAdvancedOpen ? "rotate-180" : ""}`}>
                    <FiChevronDown className="text-gray-500" />
                  </div>
                </button>
                
                {isAdvancedOpen && (
                  <div className="p-6 space-y-6 border-t border-gray-200">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <label className="block">
                        <span className="text-sm font-medium text-gray-900">
                          Danh sách từ khóa cấm (Blocked keywords)
                          <TooltipInfo text="AI sẽ bỏ qua, không trả lời nếu bình luận chứa các từ khóa này. Phân cách bằng dấu phẩy." />
                        </span>
                        <textarea
                          value={formState.blockedKeywordsText}
                          onChange={(event) => updateForm({ blockedKeywordsText: event.target.value })}
                          rows={4}
                          className="mt-1.5 w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none"
                          placeholder="VD: lừa đảo, đắt thế, fake..."
                        />
                      </label>

                      <div className="rounded-xl border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-sm font-medium text-gray-900">Trường hợp kích hoạt bot</h3>
                          <TooltipInfo text="Quyết định xem nội dung bình luận nào thì AI mới có quyền trả lời. Bỏ chọn nếu không muốn bot trả lời sai luồng." />
                        </div>
                        <div className="space-y-3">
                          {[
                            ["price", "Khách hỏi Giá / Báo giá"],
                            ["availability", "Khách hỏi Tồn kho / Tình trạng hàng"],
                            ["contact_request", "Khách muốn Liên hệ / Mua hàng / Inbox"],
                            ["generic_engagement", "Tương tác chung (khen, nhắc bạn) ⚠️ Rủi ro cao"],
                          ].map(([key, label]) => (
                            <label key={key} className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(formState.autoReplyPolicies[key])}
                                onChange={(event) => updatePolicies(key, event.target.checked)}
                                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300"
                              />
                              <span className="text-sm text-gray-700">{label}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-100 space-y-4">
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          Chế độ phản hồi
                          <TooltipInfo text="Chọn tốc độ phản hồi phù hợp với fanpage. Hệ thống tự động cấu hình các thông số an toàn tối ưu." />
                        </span>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                          {Object.entries(GUARDRAIL_PRESETS).map(([key, preset]) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => {
                                updateForm({ replySpeedPreset: key });
                                updateGuardrails({
                                  maxRepliesPerUserPer24h: preset.maxRepliesPerUserPer24h,
                                  maxAutoRepliesPerPostPerHour: preset.maxAutoRepliesPerPostPerHour,
                                  dailyPageReplyCap: preset.dailyPageReplyCap,
                                  cooldownPerUserMinutes: preset.cooldownPerUserMinutes,
                                  minDelayBetweenRepliesSeconds: preset.minDelayBetweenRepliesSeconds,
                                });
                              }}
                              className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                                formState.replySpeedPreset === key
                                  ? "border-blue-500 bg-blue-50 shadow-sm"
                                  : "border-gray-200 hover:border-gray-300 bg-white"
                              }`}
                            >
                              <div className="text-lg mb-1">{preset.label}</div>
                              <p className="text-xs text-gray-500 leading-relaxed">{preset.description}</p>
                              <div className="mt-2 text-xs text-gray-400">
                                ~{preset.dailyPageReplyCap} reply/ngày • {preset.minDelayBetweenRepliesSeconds}s/reply
                              </div>
                              {formState.replySpeedPreset === key && (
                                <div className="absolute top-2 right-2">
                                  <FiCheckCircle className="w-5 h-5 text-blue-500" />
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>

                      <details className="group">
                        <summary className="flex items-center gap-2 cursor-pointer text-xs text-gray-400 hover:text-gray-600 transition-colors select-none py-1">
                          <FiChevronDown className="w-3 h-3 group-open:rotate-180 transition-transform" />
                          Tuỳ chỉnh chi tiết (nâng cao)
                        </summary>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mt-3 p-4 bg-gray-50 rounded-xl">
                          {[
                            ["maxRepliesPerUserPer24h", "Reply / Người / 24h", 1, 20],
                            ["maxAutoRepliesPerPostPerHour", "Reply / Bài viết / Giờ", 1, 30],
                            ["dailyPageReplyCap", "Hạn mức ngày / Page", 5, 200],
                            ["cooldownPerUserMinutes", "Cooldown (Phút)", 0, 60],
                            ["minDelayBetweenRepliesSeconds", "Khoảng cách (Giây)", 10, 300],
                          ].map(([field, label, min, max]) => (
                            <label key={field} className="block">
                              <span className="text-xs font-medium text-gray-600">{label}</span>
                              <input
                                type="number" min={min} max={max}
                                value={formState.guardrails[field]}
                                onChange={(event) => {
                                  updateGuardrails({ [field]: event.target.value });
                                  updateForm({ replySpeedPreset: "custom" });
                                }}
                                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 outline-none text-sm bg-white"
                              />
                            </label>
                          ))}
                        </div>
                      </details>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-gray-100">
                      <div className="rounded-xl border border-gray-200 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-medium text-gray-900">Giờ nghỉ (Không trả lời tự động)</h3>
                            <TooltipInfo text="Tạm ngưng phản hồi Auto trong khoản thời gian xác định (ví dụ nửa đêm)." />
                          </div>
                          <ToggleSwitch
                            checked={Boolean(formState.guardrails.quietHours.enabled)}
                            onChange={(checked) => updateQuietHours({ enabled: checked })}
                          />
                        </div>
                        <div className={`grid grid-cols-2 gap-3 transition-opacity ${!formState.guardrails.quietHours.enabled ? "opacity-50 pointer-events-none" : ""}`}>
                          <label className="block">
                            <span className="text-xs text-gray-500">Giờ bắt đầu</span>
                            <input
                              type="time"
                              value={formState.guardrails.quietHours.start}
                              onChange={(event) => updateQuietHours({ start: event.target.value })}
                              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none"
                            />
                          </label>
                          <label className="block">
                            <span className="text-xs text-gray-500">Giờ kết thúc</span>
                            <input
                              type="time"
                              value={formState.guardrails.quietHours.end}
                              onChange={(event) => updateQuietHours({ end: event.target.value })}
                              className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm outline-none"
                            />
                          </label>
                        </div>
                      </div>

                      <div className="rounded-xl border border-gray-200 p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <h3 className="text-sm font-medium text-gray-900">Xử lý thủ công các case nhạy cảm</h3>
                          <TooltipInfo text="Đánh dấu các tình huống nhạy cảm để bạn vào bài viết xử lý trực tiếp, thay vì cho hệ thống tự trả lời ngay." />
                        </div>
                        <div className="space-y-3">
                          <label className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={Boolean(formState.guardrails.requiresReviewForLeadHot)}
                              onChange={(event) => updateGuardrails({ requiresReviewForLeadHot: event.target.checked })}
                              className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-sm text-gray-700">Lead Hotline/VIP (Tiềm năng cao) tạo cảnh báo để bạn tự xử lý</span>
                          </label>
                          <label className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={Boolean(formState.guardrails.requiresReviewForSupportAmbiguous)}
                              onChange={(event) => updateGuardrails({ requiresReviewForSupportAmbiguous: event.target.checked })}
                              className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-gray-300"
                            />
                            <span className="text-sm text-gray-700">Câu hỏi kỹ thuật khó, chưa hiểu sẽ báo để bạn vào bài viết xử lý</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeMainTab === "monitoring" && (
            <div className="space-y-6 pt-4">
        <div className="space-y-6">
          <Link
            to="/lead-dashboard"
            className="flex items-center justify-between rounded-2xl border border-cyan-100 bg-cyan-50 px-5 py-4 text-cyan-900 transition hover:border-cyan-200 hover:bg-cyan-100/70"
          >
            <div>
              <p className="text-sm font-semibold">Dashboard lead đầy đủ</p>
              <p className="mt-1 text-sm text-cyan-700">
                Xem funnel lead, hot lead pending và page nào đang tạo cơ hội
                tốt nhất.
              </p>
            </div>
            <span className="text-sm font-semibold">Mở dashboard →</span>
          </Link>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {[
              {
                label: "Comment nhận",
                value: leadAnalytics?.overview?.totalReceived || 0,
                tone: "bg-slate-50 text-slate-900 border-slate-200",
              },
              {
                label: "Engine đã xử lý",
                value:
                  Number(leadAnalytics?.overview?.totalSent || 0) +
                  Number(leadAnalytics?.overview?.totalManualFollowUp || 0),
                tone: "bg-cyan-50 text-cyan-900 border-cyan-200",
              },
              {
                label: "Lead surfaced",
                value: leadAnalytics?.overview?.totalLeads || 0,
                tone: "bg-amber-50 text-amber-900 border-amber-200",
              },
              {
                label: "Hot lead cần chạm ngay",
                value: leadAnalytics?.overview?.pendingHotLeadCount || 0,
                tone: "bg-rose-50 text-rose-900 border-rose-200",
              },
            ].map((item) => (
              <div
                key={item.label}
                className={`rounded-2xl border p-4 ${item.tone}`}
              >
                <p className="text-sm opacity-80">{item.label}</p>
                <p className="mt-2 text-2xl font-semibold">
                  {Number(item.value || 0).toLocaleString("vi-VN")}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Page tạo cơ hội tốt nhất
            </h2>
            <div className="space-y-3">
              {pageStats.length === 0 && (
                <p className="text-sm text-gray-500">
                  Chưa có dữ liệu lead cho page đã chọn.
                </p>
              )}
              {pageStats.map((stat) => (
                <div
                  key={stat.pageId}
                  className="rounded-2xl border border-gray-200 p-4"
                >
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-medium text-gray-900">{stat.pageName}</p>
                    <span className="text-xs text-gray-500">{stat.pageId}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="rounded-xl bg-slate-50 px-3 py-2">
                      <p className="text-slate-500">Comment nhận</p>
                      <p className="font-semibold text-slate-900">{stat.received}</p>
                    </div>
                    <div className="rounded-xl bg-cyan-50 px-3 py-2">
                      <p className="text-cyan-700">Engine đã xử lý</p>
                      <p className="font-semibold text-cyan-900">
                        {Number(stat.sent || 0) + Number(stat.manualFollowUp || 0)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-amber-50 px-3 py-2">
                      <p className="text-amber-600">Lead surfaced</p>
                      <p className="font-semibold text-amber-900">
                        {Number(stat.warmLeads || 0) + Number(stat.hotLeads || 0)}
                      </p>
                    </div>
                    <div className="rounded-xl bg-rose-50 px-3 py-2">
                      <p className="text-rose-600">Lead nóng</p>
                      <p className="font-semibold text-rose-900">{stat.hotLeads}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
                    <span>
                      Lead rate:{" "}
                      <span className="font-semibold text-gray-900">
                        {Number(stat.leadRate || 0).toLocaleString("vi-VN", {
                          minimumFractionDigits: stat.leadRate % 1 ? 1 : 0,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </span>
                    </span>
                    <span>
                      Cần sale xử lý:{" "}
                      <span className="font-semibold text-amber-900">
                        {stat.pendingManualFollowUp}
                      </span>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>


          </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-wrap gap-2 mb-6">
            {[
            {
              key: "manual_followup",
              label: "Cần sale xử lý",
              count: leadAnalytics?.overview?.currentStatusCounts?.manualFollowUp || 0,
            },
            {
              key: "sent",
              label: "AI đã reply",
              count: leadAnalytics?.overview?.currentStatusCounts?.sent || 0,
            },
            {
              key: "ignored",
              label: "Bỏ qua",
              count: leadAnalytics?.overview?.currentStatusCounts?.ignored || 0,
            },
            {
              key: "failed",
              label: "Lỗi",
              count: leadAnalytics?.overview?.currentStatusCounts?.failed || 0,
            },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                activeTab === tab.key
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {currentTabCount > currentTabThreads.length && (
          <p className="mb-4 text-xs text-gray-500">
            Đang hiển thị {currentTabThreads.length} / {currentTabCount} case gần nhất
            để giữ tải dữ liệu ổn định.
          </p>
        )}

        {activeTab === "manual_followup" && (
          <div className="space-y-4">
            {manualFollowUpThreads.length === 0 && (
              <p className="text-sm text-gray-500">Không có trường hợp cần xử lý thủ công.</p>
            )}
            {manualFollowUpThreads.map((thread) => {
              const commentLink =
                thread.manualFollowUp?.commentLink ||
                buildFacebookThreadLink({
                  pageId: thread.pageId,
                  postId: thread.postId,
                  commentId: thread.commentId,
                });
              const postLink =
                thread.manualFollowUp?.postLink ||
                buildFacebookThreadLink({
                  pageId: thread.pageId,
                  postId: thread.postId,
                });
              const primaryLink = commentLink || postLink;
              const reason =
                thread.manualFollowUp?.reason ||
                thread.lastDecision?.reason ||
                "Engine đã chuyển case này sang xử lý thủ công";

              return (
                <div
                  key={`${thread.pageId}-${thread.threadKey}`}
                className="rounded-2xl border border-amber-200/60 bg-white p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-semibold text-xs border border-gray-200">US</div>
                      <div>
                        <p className="text-xs text-gray-900 font-medium">Khách hàng • Page: {thread.pageId}</p>
                        <p className="text-xs text-gray-500">{formatRelativeTime(thread.updatedAt || thread.createdAt)}</p>
                      </div>
                    </div>
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200/60 text-xs font-medium">
                      <FiAlertCircle className="w-3.5 h-3.5" />
                      {reason}
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <div className="flex-1 bg-gray-50 rounded-2xl rounded-tl-sm p-4 w-full shadow-sm border border-gray-100/60">
                      <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {thread.latestMessage || thread.lastInbound?.message || "(không có nội dung)"}
                      </p>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3">
                    <p className="text-sm text-blue-900 font-medium">
                      Engine không tự gửi phản hồi ở case này.
                    </p>
                    <p className="text-sm text-blue-700 mt-1">
                      Bạn mở bài viết hoặc bình luận trên Facebook để trả lời trực tiếp. Hệ thống chỉ giữ lại cảnh báo để bạn xử lý nhanh hơn.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-between border-t border-gray-100 pt-4 mt-2">
                    <div className="text-xs text-gray-600 flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-full border border-gray-200/60">
                      <span className={`w-2 h-2 rounded-full ${thread.leadState === 'hot' ? 'bg-rose-500' : thread.leadState === 'cold' ? 'bg-slate-400' : 'bg-orange-400'}`}></span>
                      Lead state: <span className="font-semibold text-gray-900 capitalize">{thread.leadState || "warm"}</span>
                    </div>

                    {primaryLink ? (
                      <div className="flex flex-wrap gap-2.5">
                        <a
                          href={primaryLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 shadow-md shadow-blue-600/20 transition-all text-sm font-medium"
                        >
                          <FiExternalLink className="w-4 h-4" />
                          Vào Facebook để phản hồi
                        </a>
                        {postLink && commentLink && postLink !== commentLink && (
                          <a
                            href={postLink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm transition-all text-sm font-medium"
                          >
                            <FiExternalLink className="w-4 h-4" />
                            Mở bài viết
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-3 py-1.5">
                        Chưa tạo được link Facebook cho thread này
                      </span>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}

        {activeTab !== "manual_followup" && (
          <div className="space-y-4">
            {currentTabThreads.map((thread) => (
              <div
                key={`${thread.pageId}-${thread.threadKey}`}
                className="rounded-2xl border border-gray-200 p-4"
              >
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          thread.status === "sent"
                            ? "bg-emerald-100 text-emerald-700"
                            : thread.status === "ignored"
                            ? "bg-slate-100 text-slate-700"
                            : "bg-rose-100 text-rose-700"
                        }`}
                      >
                        {thread.status === "sent" ? <FiCheckCircle /> : thread.status === "ignored" ? <FiClock /> : <FiAlertCircle />}
                        {thread.status}
                      </span>
                      <span className="text-xs text-gray-500">
                        Page {thread.pageId}
                      </span>
                    </div>
                    <p className="font-medium text-gray-900">
                      {thread.latestMessage || "(không có nội dung)"}
                    </p>
                    <p className="text-sm text-gray-500">
                      Lead state: {thread.leadState || "cold"} • Cập nhật{" "}
                      {formatRelativeTime(thread.updatedAt)}
                    </p>
                    {thread.lastReply?.message && (
                      <p className="text-sm text-emerald-700 bg-emerald-50 rounded-xl p-3">
                        Reply: {thread.lastReply.message}
                      </p>
                    )}
                    {thread.error?.message && (
                      <p className="text-sm text-rose-700 bg-rose-50 rounded-xl p-3">
                        Lỗi: {thread.error.message}
                      </p>
                    )}
                  </div>
                  {thread.status === "failed" && (
                    <button
                      onClick={() => handleRetryThread(thread)}
                      disabled={Boolean(processingAction) || isCommentFeatureLocked}
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
                    >
                      <FiRefreshCw />
                      Retry
                    </button>
                  )}
                </div>
              </div>
            ))}

            {activeTab === "sent" && sentThreads.length === 0 && (
              <p className="text-sm text-gray-500">Chưa có thread đã gửi.</p>
            )}
            {activeTab === "ignored" && ignoredThreads.length === 0 && (
              <p className="text-sm text-gray-500">Chưa có thread bị bỏ qua.</p>
            )}
            {activeTab === "failed" && failedThreads.length === 0 && (
              <p className="text-sm text-gray-500">Chưa có thread thất bại.</p>
            )}
          </div>
        )}
      </div>
    </div>
  )}
        </div>
      </div>
    </div>
  );
};

export default FacebookCommentAutomationScreen;
